import React, { useState, useRef, useEffect } from 'react';
import Icons from './Icons';
import { generateEmlContent, generateDailyEmlContent } from '../utils/emailUtils';
import { generateEmailSafeHtml, generateDailyEmailSafeHtml } from '../utils/emailUtilsSafe';
import apiClient from '../utils/apiClient';
import { API_BASE_URL } from '../utils/apiConfig';

const EmailComposer = ({
  isOpen,
  onClose,
  recipients = [],
  subject = '',
  eventDetails,
  pairings = [],
  eventPlayers = [],
  upcomingEvents = [],
  weatherData = null,
  adminUser = null,
  emailType = 'pairings',
  enableSendFeature = false // Feature flag - set to true to enable email sending
}) => {
  const [emailData, setEmailData] = useState({
    to: recipients.join(', '),
    subject: subject,
    htmlContent: '',
    loading: true
  });

  const [customMessage, setCustomMessage] = useState('');

  const [sending, setSending] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editableHtml, setEditableHtml] = useState('');
  const iframeRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      generateEmailContent();
    }
  }, [isOpen, customMessage]);

  const generateEmailContent = async () => {
    try {
      const isDaily = emailType === 'daily';

      // Get logo base64 from the correct path
      let logoBase64 = null;
      try {
        const logoResponse = await fetch('/assets/logo.png');
        if (logoResponse.ok) {
          const blob = await logoResponse.blob();
          const reader = new FileReader();
          logoBase64 = await new Promise((resolve) => {
            reader.onload = () => {
              const dataUrl = reader.result;
              resolve(dataUrl.split(',')[1]);
            };
            reader.readAsDataURL(blob);
          });
          console.log('Logo loaded from /assets/logo.png');
        }
      } catch (error) {
        console.log('Could not load logo for email');
      }

      let htmlContent;
      if (isDaily) {
        htmlContent = generateDailyEmailSafeHtml({
          eventDetails,
          eventPlayers,
          upcomingEvents,
          weatherData,
          logoBase64,
          customMessage
        });
      } else {
        htmlContent = generateEmailSafeHtml({
          eventDetails,
          pairings,
          eventPlayers,
          weatherData,
          logoBase64,
          customMessage
        });
      }

      setEmailData({
        to: recipients.join(', '),
        subject: subject,
        htmlContent: htmlContent,
        loading: false
      });

      setEditableHtml(htmlContent);
    } catch (error) {
      console.error('Failed to generate email content:', error);
      setEmailData(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const response = await apiClient.post('/admin/email/send', {
        to: emailData.to.split(',').map(email => email.trim()),
        subject: emailData.subject,
        html: editMode ? editableHtml : emailData.htmlContent,
        from: 'info@gulfcoasthackers.com', // Always use verified email
        replyTo: adminUser?.email // Replies will go to the admin
      });

      if (response.success) {
        alert('Email sent successfully!');
        onClose();
      } else {
        throw new Error(response.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(`Failed to send email: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const toggleEditMode = () => {
    if (editMode) {
      // Save changes from editor
      if (editorRef.current) {
        setEditableHtml(editorRef.current.value);
      }
    }
    setEditMode(!editMode);
  };

  const handleCopySubject = async () => {
    try {
      await navigator.clipboard.writeText(emailData.subject);
      alert('Subject copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy subject:', error);
      alert('Failed to copy subject.');
    }
  };

  const handleCopyRecipients = async () => {
    try {
      await navigator.clipboard.writeText(emailData.to);
      alert('Recipients copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy recipients:', error);
      alert('Failed to copy recipients.');
    }
  };

  const handleCopyEmailBody = async () => {
    const htmlContent = editMode ? editableHtml : emailData.htmlContent;

    try {
      // Create a plain text version for fallback
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const plainText = tempDiv.innerText || tempDiv.textContent || '';

      // Try to use the modern Clipboard API with multiple formats
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const plainBlob = new Blob([plainText], { type: 'text/plain' });

        const clipboardItem = new ClipboardItem({
          'text/html': blob,
          'text/plain': plainBlob
        });

        await navigator.clipboard.write([clipboardItem]);
        alert('Email body copied! You can now paste it into your email client.');
      } else {
        // Fallback for older browsers - copy as rich text using a contenteditable div
        const tempDiv = document.createElement('div');
        tempDiv.contentEditable = true;
        tempDiv.innerHTML = htmlContent;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);

        // Select all content
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        // Copy to clipboard
        document.execCommand('copy');

        // Clean up
        document.body.removeChild(tempDiv);
        selection.removeAllRanges();

        alert('Email body copied! You can now paste it into your email client.');
      }
    } catch (error) {
      console.error('Failed to copy email:', error);

      // Final fallback - copy just the HTML as text
      try {
        await navigator.clipboard.writeText(htmlContent);
        alert('HTML copied as text. You may need to use your email client\'s HTML mode to paste this.');
      } catch (fallbackError) {
        alert('Failed to copy email body. Please try selecting and copying manually.');
      }
    }
  };

  const handleCopyAll = async () => {
    try {
      // Create a mailto link with all the data
      const mailtoLink = `mailto:${emailData.to}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent('Please paste the email body here')}`;

      // Copy all three items to clipboard with labels
      const fullText = `To: ${emailData.to}\nSubject: ${emailData.subject}\n\nNote: Copy the email body separately using the 'Copy Email Body' button for formatted content.`;

      await navigator.clipboard.writeText(fullText);
      alert('Recipients and subject copied! Use the "Copy Email Body" button to copy the formatted content.');

      // Optionally open mailto link
      if (window.confirm('Would you like to open your default email client with these details?')) {
        window.location.href = mailtoLink;
      }
    } catch (error) {
      console.error('Failed to copy all:', error);
      alert('Failed to copy email details.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center">
              <Icons.Mail className="w-5 h-5 mr-2" />
              Email Composer
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <Icons.Close className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Email Fields */}
        <div className="p-4 border-b bg-gray-50">
          <div className="space-y-3">
            <div className="flex items-center">
              <label className="font-semibold w-20">To:</label>
              <input
                type="text"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                className="flex-1 px-3 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="recipient@example.com, another@example.com"
              />
            </div>
            <div className="flex items-center">
              <label className="font-semibold w-20">Subject:</label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                className="flex-1 px-3 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Email subject"
              />
            </div>
            <div className="flex items-start">
              <label className="font-semibold w-20 pt-2">Message:</label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Add a custom message at the beginning of the email (optional)"
                rows="3"
              />
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden p-4">
          {emailData.loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Generating email content...</div>
            </div>
          ) : editMode ? (
            <textarea
              ref={editorRef}
              value={editableHtml}
              onChange={(e) => setEditableHtml(e.target.value)}
              className="w-full h-full p-4 border rounded-md font-mono text-sm"
              placeholder="Edit HTML content here..."
            />
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={editableHtml || emailData.htmlContent}
              className="w-full h-full border rounded-md bg-white"
              title="Email Preview"
            />
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex gap-2">
            {/* Copy buttons - always visible */}
            {/* <button
              onClick={handleCopyAll}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center"
              title="Copy recipients and subject, then optionally open email client"
            >
              <Icons.Mail className="w-4 h-4 mr-2" />
              Copy All & Open Mail
            </button> */}

            <button
              onClick={handleCopyEmailBody}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
              title="Copy formatted email body to paste in your email client"
            >
              <Icons.Copy className="w-4 h-4 mr-2" />
              Copy Email Body
            </button>

            <button
              onClick={handleCopySubject}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center"
              title="Copy subject line"
            >
              <Icons.Copy className="w-4 h-4 mr-2" />
              Copy Subject
            </button>

            <button
              onClick={handleCopyRecipients}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-2 rounded-lg flex items-center"
              title="Copy recipient emails"
            >
              <Icons.Copy className="w-4 h-4 mr-2" />
              Copy Recipients
            </button>

            {/* <button
              onClick={toggleEditMode}
              className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
                editMode 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
            >
              {editMode ? (
                <>
                  <Icons.Check className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              ) : (
                <>
                  <Icons.Edit className="w-4 h-4 mr-2" />
                  Edit HTML
                </>
              )}
            </button> */}

            {/* <button
              onClick={() => {
                const blob = new Blob([editableHtml || emailData.htmlContent], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'email-preview.html';
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <Icons.Download className="w-4 h-4 mr-2" />
              Download HTML
            </button> */}
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
            >
              Close
            </button>

            {/* Send button - only visible when feature is enabled */}
            {enableSendFeature && (
              <button
                onClick={handleSendEmail}
                disabled={sending || !emailData.to || !emailData.subject}
                className={`px-6 py-2 rounded-lg flex items-center font-semibold transition-colors ${sending || !emailData.to || !emailData.subject
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
              >
                {sending ? (
                  <>
                    <span className="inline-block h-4 w-4 rounded-full bg-white/70 animate-pulse mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Icons.Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
